import React, { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DataProvidersMessage } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/model/DataProvidersMessage';
import { DataProviderTypeMessage } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/model/DataProviderTypeMessage';
import { SellerMessage } from '@1po/1po-bff-fe-spec/generated/backoffice/central_settings/model/SellerMessage';
import { DeleteIcon, EditIcon } from 'assets/icons';
import {
  addSellerRequested,
  getSellerCommandStatus,
  setSellerCommandStatus,
  updateSellerDataProvidersRequested,
} from 'domains/centralSettings/CentralSettings.store';
import { theme } from 'styles';
import { Flex, Icon, Input, RadioGroup, Text, WhiteButton } from 'UI';
import { SellerCard, SellerNameRow } from './SellerManagement.styled';

interface SellerFormProps {
  countryCode: string;
  seller?: SellerMessage;
  editing: boolean;
  editDisabled?: boolean;
  onEdit?: () => void;
  onDiscard: () => void;
  onSaved: () => void;
}

const PROVIDER_RENAULT = 'RENAULT';
const PROVIDER_MARKETPLACE = 'MARKETPLACE';

const DEFAULT_DATA_PROVIDERS: DataProvidersMessage = {
  priceProvider: PROVIDER_RENAULT,
  stockProvider: PROVIDER_RENAULT,
  orderProvider: PROVIDER_RENAULT,
};

const SELLER_NAME_PATTERN = /^[A-Za-z0-9]+$/;
const PROVIDER_OPTIONS: DataProviderTypeMessage[] = [PROVIDER_RENAULT, PROVIDER_MARKETPLACE];

const SellerForm = ({ countryCode, seller, editing, editDisabled, onEdit, onDiscard, onSaved }: SellerFormProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const sellerCommandStatus = useSelector(getSellerCommandStatus);
  const [name, setName] = useState('');
  const [dataProviders, setDataProviders] = useState<DataProvidersMessage>(DEFAULT_DATA_PROVIDERS);
  const [nameTouched, setNameTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setName(seller?.name ?? '');
    setDataProviders(seller?.dataProviders ?? DEFAULT_DATA_PROVIDERS);
    setNameTouched(false);
    setSubmitted(false);
    if (editing) {
      dispatch(setSellerCommandStatus('IDLE'));
    }
  }, [dispatch, seller, editing]);

  useEffect(() => {
    if (editing && submitted && sellerCommandStatus === 'SUCCESS') {
      onSaved();
    }
  }, [editing, onSaved, sellerCommandStatus, submitted]);

  useEffect(() => {
    if (sellerCommandStatus === 'ERROR') {
      setSubmitted(false);
    }
  }, [sellerCommandStatus]);

  const isValid = SELLER_NAME_PATTERN.test(name.trim()) && Object.values(dataProviders).every(Boolean);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNameTouched(true);
    const sellerName = name.trim();
    if (submitted || !SELLER_NAME_PATTERN.test(sellerName) || !Object.values(dataProviders).every(Boolean)) {
      return;
    }

    setSubmitted(true);
    if (seller) {
      dispatch(
        updateSellerDataProvidersRequested({
          countryCode,
          sellerName,
          dataProviders,
        }),
      );
    } else {
      dispatch(
        addSellerRequested({
          countryCode,
          sellerName,
          dataProviders,
        }),
      );
    }
  };

  const providerFields: { key: keyof DataProvidersMessage; label: string }[] = [
    { key: 'priceProvider', label: t('backoffice.central-settings.sellers.price', 'Price') },
    { key: 'stockProvider', label: t('backoffice.central-settings.sellers.stock', 'Stock') },
    { key: 'orderProvider', label: t('backoffice.central-settings.sellers.order', 'Order') },
  ];

  return (
    <SellerCard direction="column" gap={14} data-cy={seller ? `seller-row-${seller.name}` : 'seller-form'}>
      <form onSubmit={handleSubmit}>
        <SellerNameRow size={0}>
          <Input
            id={seller ? `seller-name-${seller.name}` : 'seller-form-name'}
            value={name}
            onChange={(value) => setName(value.replaceAll(/[^A-Za-z0-9]/g, ''))}
            onBlur={() => setNameTouched(true)}
            data-cy={seller ? `seller-name-${seller.name}` : 'seller-form-name'}
            readOnly={Boolean(seller)}
            bordered={false}
            placeholder={t('backoffice.central-settings.sellers.form.name-label', 'Seller name')}
            maxLength={30}
            size={'large'}
          />
          <Flex size={0} gap={4} padding={'0 16px'}>
            <Icon
              IconComponent={DeleteIcon}
              size={20}
              color={theme.color.error}
              onClick={onDiscard}
              data-cy={seller ? `seller-delete-${seller.name}` : 'seller-form-discard'}
            />
            <Icon
              IconComponent={EditIcon}
              size={20}
              color={theme.color.link_default_solid}
              onClick={seller ? onEdit : undefined}
              disabled={Boolean(seller && (editing || editDisabled))}
              data-cy={seller ? `seller-edit-${seller.name}` : undefined}
            />
          </Flex>
        </SellerNameRow>
        {nameTouched && !SELLER_NAME_PATTERN.test(name.trim()) && (
          <Text type="small" color={'error'}>
            {t('backoffice.central-settings.sellers.form.name-invalid', 'Use letters and numbers only')}
          </Text>
        )}
        <Flex direction="column" gap={16} size={0}>
          <Text type="heading5">
            {t('backoffice.central-settings.sellers.select-data-provider', 'SELECT DATA PROVIDER')}
          </Text>
          {providerFields.map(({ key, label }) => (
            <Flex key={key} direction="column" gap={8} size={0}>
              <Text type="heading5" disableGutter id={`${seller?.name ?? 'new'}-${key}-label`}>
                {label}
              </Text>
              <RadioGroup
                aria-labelledby={`${seller?.name ?? 'new'}-${key}-label`}
                name={`${seller?.name ?? 'new'}-${key}`}
                value={dataProviders[key]}
                disabled={!editing || submitted}
                onChange={(event) =>
                  setDataProviders((current) => ({ ...current, [key]: event.target.value as DataProviderTypeMessage }))
                }
                options={PROVIDER_OPTIONS.map((provider) => ({
                  value: provider,
                  label: (
                    <Text type="lg">
                      {provider === 'RENAULT'
                        ? t('backoffice.central-settings.sellers.provider.renault', 'RENAULT')
                        : t('backoffice.central-settings.sellers.provider.marketplace', 'MARKETPLACE')}
                    </Text>
                  ),
                }))}
                data-cy={`seller-form-${key}`}
              />
            </Flex>
          ))}
          {editing && (
            <WhiteButton htmlType="submit" disabled={!isValid || submitted} data-cy="seller-form-save" stretch>
              {t('backoffice.central-settings.sellers.confirm', 'Confirm')}
            </WhiteButton>
          )}
        </Flex>
      </form>
    </SellerCard>
  );
};

export default SellerForm;
